import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, Package, Truck, Clock, Zap, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { safeFetchCompat as fetch } from '../utils/safeFetch';

const QuotePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [quoteData, setQuoteData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    fetchQuote();
  }, [token]);

  useEffect(() => {
    if (quoteData && !quoteData.is_expired) {
      const interval = setInterval(() => {
        const expiresAt = new Date(quoteData.expires_at);
        const now = new Date();
        const diff = expiresAt - now;
        
        if (diff <= 0) {
          setQuoteData(prev => ({ ...prev, is_expired: true }));
          clearInterval(interval);
        } else {
          const minutes = Math.floor(diff / 60000);
          const seconds = Math.floor((diff % 60000) / 1000);
          setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [quoteData]);

  const fetchQuote = async () => {
    try {
      const response = await fetch(`${API_URL}/api/leads/quote/${token}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Quote not found');
        } else {
          throw new Error('Failed to load quote');
        }
        return;
      }
      
      const data = await response.json();
      setQuoteData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <main className="bg-[#F8FAFC] min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#0D9488]" />
      </main>
    );
  }

  if (error) {
    return (
      <main className="bg-[#F8FAFC] min-h-screen py-12">
        <div className="max-w-xl mx-auto px-4 text-center">
          <Card className="border-[#E2E8F0]">
            <CardContent className="p-12">
              <h2 className="text-2xl font-bold text-[#111111]">Quote Not Found</h2>
              <p className="text-[#64748B] mt-4">This pricing link may have expired or is invalid.</p>
              <Link to="/pricing">
                <Button className="mt-8 bg-[#0D9488] hover:bg-[#0F766E] text-white">
                  Request New Pricing
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  // Expired - redirect to pricing form
  if (quoteData.is_expired) {
    return (
      <main className="bg-[#F8FAFC] min-h-screen py-12">
        <div className="max-w-xl mx-auto px-4 text-center">
          <Card className="border-[#E2E8F0]">
            <CardContent className="p-12">
              <div className="w-16 h-16 rounded-full bg-yellow-50 flex items-center justify-center mx-auto mb-6">
                <Clock className="w-8 h-8 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-bold text-[#111111]">Pricing Has Expired</h2>
              <p className="text-[#64748B] mt-4">
                Hi {quoteData.name}, this pricing link has expired. Please request a new quote to see our current pricing.
              </p>
              <Link to="/pricing">
                <Button className="mt-8 bg-[#0D9488] hover:bg-[#0F766E] text-white h-12 px-8">
                  Request Pricing
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  const serviceIcons = {
    standard: Clock,
    subscription: Package,
    priority: Zap
  };

  const serviceColors = {
    standard: { bg: 'bg-blue-50', icon: 'text-blue-600', border: 'border-blue-200' },
    subscription: { bg: 'bg-[#F0FDFA]', icon: 'text-[#0D9488]', border: 'border-[#0D9488]' },
    priority: { bg: 'bg-yellow-50', icon: 'text-yellow-600', border: 'border-yellow-200' }
  };

  return (
    <main className="bg-[#F8FAFC] min-h-screen py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-[#111111]">
            Hi {quoteData.name}!
          </h1>
          <p className="text-lg text-[#64748B] mt-3">
            As requested, here is our pricing for {quoteData.service_interest === 'pickup' ? 'packaging collection' : 'delivery'} services.
          </p>
          
          {/* Timer */}
          <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-full">
            <Clock className="w-4 h-4 text-yellow-600" />
            <span className="text-yellow-700 text-sm font-medium">
              Pricing valid for: <strong>{timeRemaining}</strong>
            </span>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {quoteData.pricing.map((item) => {
            const ServiceIcon = serviceIcons[item.service_type] || Package;
            const colors = serviceColors[item.service_type] || serviceColors.standard;
            const isPopular = item.service_type === 'subscription';

            return (
              <Card 
                key={item.service_type}
                className={`relative border-2 ${isPopular ? colors.border : 'border-[#E2E8F0]'} ${isPopular ? 'ring-2 ring-[#0D9488] ring-offset-2' : ''}`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#0D9488] text-white text-xs font-bold px-3 py-1 rounded-full">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                <CardContent className="p-6 pt-8">
                  <div className={`w-14 h-14 rounded-xl ${colors.bg} flex items-center justify-center mb-4`}>
                    <ServiceIcon className={`w-7 h-7 ${colors.icon}`} />
                  </div>
                  
                  <h3 className="text-xl font-bold text-[#111111]">{item.name}</h3>
                  <p className="text-sm text-[#64748B] mt-2 min-h-[48px]">{item.description}</p>
                  
                  <div className="mt-6 mb-6">
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg text-[#94A3B8] line-through">
                        ${(item.price * 1.25).toFixed(2)}
                      </span>
                      <span className="text-4xl font-bold text-[#111111]">${item.price.toFixed(2)}</span>
                      <span className="text-[#64748B]">AUD</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="bg-red-100 text-red-600 text-xs font-bold px-2 py-0.5 rounded">
                        SAVE 20%
                      </span>
                      <p className="text-xs text-[#94A3B8]">Inc. GST</p>
                    </div>
                  </div>

                  <ul className="space-y-2 mb-6">
                    {item.service_type === 'standard' && (
                      <>
                        <li className="flex items-center gap-2 text-sm text-[#64748B]">
                          <CheckCircle className="w-4 h-4 text-[#0D9488]" />
                          Flexible scheduling
                        </li>
                        <li className="flex items-center gap-2 text-sm text-[#64748B]">
                          <CheckCircle className="w-4 h-4 text-[#0D9488]" />
                          Collection within 7 days
                        </li>
                        <li className="flex items-center gap-2 text-sm text-[#64748B]">
                          <CheckCircle className="w-4 h-4 text-[#0D9488]" />
                          Photo confirmation
                        </li>
                      </>
                    )}
                    {item.service_type === 'subscription' && (
                      <>
                        <li className="flex items-center gap-2 text-sm text-[#64748B]">
                          <CheckCircle className="w-4 h-4 text-[#0D9488]" />
                          Best value for regulars
                        </li>
                        <li className="flex items-center gap-2 text-sm text-[#64748B]">
                          <CheckCircle className="w-4 h-4 text-[#0D9488]" />
                          Up to 5 jobs per week
                        </li>
                        <li className="flex items-center gap-2 text-sm text-[#64748B]">
                          <CheckCircle className="w-4 h-4 text-[#0D9488]" />
                          Priority support
                        </li>
                      </>
                    )}
                    {item.service_type === 'priority' && (
                      <>
                        <li className="flex items-center gap-2 text-sm text-[#64748B]">
                          <CheckCircle className="w-4 h-4 text-[#0D9488]" />
                          Premium service
                        </li>
                        <li className="flex items-center gap-2 text-sm text-[#64748B]">
                          <CheckCircle className="w-4 h-4 text-[#0D9488]" />
                          Collection within 48 hours
                        </li>
                        <li className="flex items-center gap-2 text-sm text-[#64748B]">
                          <CheckCircle className="w-4 h-4 text-[#0D9488]" />
                          Dedicated support
                        </li>
                      </>
                    )}
                  </ul>

                  <Link to="/book/waste">
                    <Button 
                      className={`w-full h-12 ${
                        isPopular 
                          ? 'bg-[#0D9488] hover:bg-[#0F766E] text-white' 
                          : 'bg-white border-2 border-[#E2E8F0] text-[#111111] hover:bg-[#F8FAFC]'
                      }`}
                    >
                      Book Now
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Contact CTA */}
        <Card className="mt-10 bg-gradient-to-r from-[#0D9488] to-[#0F766E] border-0">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold text-white">Have Questions?</h3>
            <p className="text-white/80 mt-2">
              Our team is here to help you choose the right plan for your business.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+61407502587">
                <Button variant="secondary" className="bg-white text-[#0D9488] hover:bg-gray-100">
                  Call Us
                </Button>
              </a>
              <Link to="/contact">
                <Button variant="outline" className="border-white text-white hover:bg-white/10">
                  Contact Form
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

export default QuotePage;
